import { format } from 'date-fns';
import { FC } from 'react';

import DeleteAttachmentPopup from './components/DeleteAttachmentPopup/DeleteAttachmentPopup';
import { ActionTypeE } from './declarations';
import useViewAttachmentsPopup from './hooks/useViewAttachmentsPopup';
import {
  AttachmentNameStyled,
  BtnStyled,
  FlexStyled,
  ListItemStyled,
  ListStyled,
  TitleStyled,
  TypographyStyled,
  getPopupContentStyle,
} from './ViewAttachmentsPopup.styled';
import ButtonIcon from 'components/ButtonIcon/ButtonIcon';
import CircleLoader from 'components/CircleLoader/CircleLoader';
import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Popup from 'components/Popup/Popup';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { StatusIdE } from 'declarations/enums/tickets';
import { NoopT } from 'declarations/types/common';
import { DeleteIcon, DownloadFileIcon, PreviewIcon } from 'icons';
import ApproveIcon from 'icons/ApproveIcon';

export type ViewAttachmentsPopupPropsT = {
  ticketId: string;
  ticketStatus: StatusIdE;
  isOpen: boolean;
  onClose: NoopT;
};

const ViewAttachmentsPopup: FC<ViewAttachmentsPopupPropsT> = ({
  ticketId,
  ticketStatus,
  isOpen,
  onClose,
}) => {
  /**
   * Hooks
   */
  const { isMobile } = useResponsive();
  const {
    attachments,
    isLoadingAttachments,
    isAttachmentsFetchingError,
    isDeleteAttachmentPopupOpened,
    selectedAttachment,
    handleOpenDeleteAttachmentPopup,
    handleCloseDeleteAttachmentPopup,
    handleAttachmentClick,
    isLoadingAttachmentForPreview,
    isLoadingAttachmentForDownload,
    isAttachmentForDownloadError,
    isAttachmentForPreviewError,
  } = useViewAttachmentsPopup({ ticketId, isPopupOpened: isOpen });

  return (
    <Popup
      nested
      showCloseBtn
      popupId={ScrollLockIdE.ViewAttachmentsPopup}
      // @ts-expect-error: ignore overflowY css property error
      contentStyle={getPopupContentStyle(isMobile)}
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape={false}
    >
      {!!attachments?.length && !isLoadingAttachments && (
        <>
          <Typography variant="h2" color="dark">
            Attached documents
          </Typography>

          <ListStyled>
            {attachments?.map((attachment, i) => {
              const isPreviewActive =
                isLoadingAttachmentForPreview &&
                selectedAttachment?.fileName === attachment.fileName;
              const isDownloadActive =
                isLoadingAttachmentForDownload &&
                selectedAttachment?.fileName === attachment.fileName;
              const isDeleteBtnDisabled = ticketStatus !== StatusIdE.Open;

              return (
                <ListItemStyled key={`attachment-${i}`}>
                  <FlexContainer
                    title={
                      attachment.approvedForClaimSubmission
                        ? 'Attachment is approved'
                        : 'Attachment is not approved'
                    }
                  >
                    <ApproveIcon
                      color={attachment.approvedForClaimSubmission ? 'brightGreen' : 'grey'}
                      size={isMobile ? 'xl' : '3xl'}
                    />
                    <AttachmentNameStyled>
                      {attachment.originalName !== undefined
                        ? attachment.originalName
                        : attachment.fileName}
                    </AttachmentNameStyled>
                  </FlexContainer>
                  <div>
                    <TitleStyled size="xs">Uploaded on</TitleStyled>
                    <TypographyStyled>
                      {format(new Date(attachment.createdDateTime), 'MMM d, yyyy')}
                    </TypographyStyled>
                  </div>
                  <div>
                    <TitleStyled size="xs">Type</TitleStyled>
                    <TypographyStyled>{attachment.attachmentTypeCode}</TypographyStyled>
                  </div>
                  <div>
                    {isMobile ? (
                      <>
                        <ButtonIcon
                          size="md"
                          variant="transparent"
                          icon={<PreviewIcon size="5xl" />}
                          disabled={isPreviewActive}
                          onClick={handleAttachmentClick(attachment, ActionTypeE.View)}
                        />
                        <ButtonIcon
                          size="md"
                          variant="transparent"
                          icon={<DownloadFileIcon size="5xl" />}
                          disabled={isDownloadActive}
                          onClick={handleAttachmentClick(attachment, ActionTypeE.Download)}
                        />
                        <ButtonIcon
                          size="md"
                          variant="transparent"
                          icon={<DeleteIcon size="5xl" />}
                          disabled={isDeleteBtnDisabled}
                          onClick={handleOpenDeleteAttachmentPopup(attachment)}
                        />
                      </>
                    ) : (
                      <>
                        <BtnStyled
                          isLoading={isPreviewActive}
                          loaderColor="darkBlue"
                          variant="text"
                          onClick={handleAttachmentClick(attachment, ActionTypeE.View)}
                        >
                          View
                        </BtnStyled>
                        <BtnStyled
                          isLoading={isDownloadActive}
                          loaderColor="darkBlue"
                          variant="text"
                          onClick={handleAttachmentClick(attachment, ActionTypeE.Download)}
                        >
                          Download
                        </BtnStyled>
                        <BtnStyled
                          variant="text"
                          disabled={isDeleteBtnDisabled}
                          onClick={handleOpenDeleteAttachmentPopup(attachment)}
                        >
                          Delete
                        </BtnStyled>
                      </>
                    )}
                  </div>
                </ListItemStyled>
              );
            })}
          </ListStyled>
        </>
      )}

      {(isAttachmentsFetchingError ||
        isAttachmentForDownloadError ||
        isAttachmentForPreviewError) && (
        <FlexContainer direction="column">
          <ErrorSection />
        </FlexContainer>
      )}

      {!attachments?.length && !isLoadingAttachments && (
        <FlexStyled justify="center">
          <Typography variant="h2" color="darkBlue">
            Attachments not found
          </Typography>
        </FlexStyled>
      )}

      {isLoadingAttachments && (
        <FlexStyled align="center" justify="center">
          <CircleLoader />
        </FlexStyled>
      )}

      <DeleteAttachmentPopup
        ticketId={ticketId}
        fileName={selectedAttachment?.fileName || ''}
        originalName={
          (selectedAttachment?.originalName !== undefined
            ? selectedAttachment?.originalName
            : selectedAttachment?.fileName) || ''
        }
        isOpen={isDeleteAttachmentPopupOpened}
        onClose={handleCloseDeleteAttachmentPopup}
      />
    </Popup>
  );
};

export default ViewAttachmentsPopup;
